import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Avatar,
    Badge,
    Box,
    Button,
    Checkbox,
    HStack,
    IconButton,
    SimpleGrid,
    Text,
    Tooltip,
    VStack,
    useColorModeValue,
    useDisclosure
} from '@chakra-ui/react';
import React, { useRef, useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import { translate } from '../../../i18n';
import { GameDto, GameParticipantDto } from '../../../models/game/dto/game.dto';
import { useIsOrgManager } from '../../auth/roles';
import { useNavigateToUserDetails } from '../../_shared/calendar/hooks/useNavigateToUserDetails';
import { formatDate } from '../GameDetailsPage';
import { useAdminRefundSeat } from '../hooks/useAdminRefundSeat';
import { useKickParticipant } from '../hooks/useKickParticipant';
import CoveredByChips from './CoveredByChips';

interface ParticipantsCardProps {
    game: GameDto;
}

const ParticipantsCard: React.FC<ParticipantsCardProps> = ({ game }) => {
    const navigateToUserDetails = useNavigateToUserDetails();
    const isOrgManager = useIsOrgManager();
    const kickParticipantMutation = useKickParticipant();
    const refundSeatMutation = useAdminRefundSeat();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [participantToRemove, setParticipantToRemove] = useState<GameParticipantDto | null>(null);
    // "Also refund their seat": off by default — the kick never refunded, and
    // an operator removing a no-show is not necessarily returning their money.
    // Reset on every open so one dialog's choice does not leak into the next.
    const [refundSeatAfterRemove, setRefundSeatAfterRemove] = useState(false);
    const cancelRef = useRef<HTMLButtonElement>(null);
    const cardBg = useColorModeValue('white', 'gray.700');
    const cardHoverBg = useColorModeValue('gray.50', 'gray.600');

    const handleRemoveClick = (participant: GameParticipantDto) => {
        setParticipantToRemove(participant);
        setRefundSeatAfterRemove(false);
        onOpen();
    };

    const isRemoving = kickParticipantMutation.isLoading;

    // The dialog used to close SYNCHRONOUSLY on the same tick the mutation was
    // fired, which had three consequences and only the first is cosmetic.
    // `isLoading` on the confirm button below could never be true while that
    // button was mounted, so the spinner was unreachable code. The operator got
    // no in-context acknowledgement that anything was happening. And a REFUSED
    // kick — a 403, a network failure, or the API answering `success: false`,
    // all three of which `useKickParticipant` toasts — tore down the dialog
    // anyway, so the only way to retry was to find the participant in the grid
    // again and re-open it, having been told the removal did not happen by a
    // toast that outlives the surface it is about.
    //
    // It now closes only once the participant is actually gone. A refusal keeps
    // the dialog up, with the toast beside it, so the retry is one click on the
    // same button.
    //
    // The in-flight guard is a REF, not `isRemoving`, and that distinction is
    // the whole of it. Keeping the dialog mounted is precisely what makes a
    // double-fire reachable for the first time — the old code nulled
    // `participantToRemove` on the first click, and that null was the thing
    // stopping the second. `isRemoving` cannot replace it: react-query flips
    // `isLoading` through a state update, so two clicks inside one frame both
    // read the PRE-flight render's `false` and both fire. A ref is written
    // synchronously and the second click sees it. This mutation is not
    // idempotent for the operator — the second call answers `success: false`
    // for a participant the first already removed, i.e. an error toast about a
    // removal that worked.
    const inFlightRef = useRef(false);

    const handleConfirmRemove = () => {
        if (!participantToRemove || inFlightRef.current) {
            return;
        }
        inFlightRef.current = true;
        kickParticipantMutation.mutate(
            {
                gameId: game.id,
                participantUserId: participantToRemove.participant.id
            },
            {
                // Released on EVERY outcome, not just success: a refusal has to
                // leave the retry button live or the fix above buys nothing.
                onSettled: () => {
                    inFlightRef.current = false;
                },
                // `useKickParticipant` owns the reporting for all three
                // outcomes; this callback owns only the dialog's lifetime, and
                // `data.success === false` is a refusal, not a success.
                onSuccess: (data) => {
                    if (data?.success) {
                        // The refund is a SECOND request, fired only once the
                        // seat is actually free: the API refuses to refund a
                        // player who is still seated. Its own hook toasts the
                        // outcome (queued / nothing to refund / error).
                        if (refundSeatAfterRemove) {
                            refundSeatMutation.mutate({
                                gameId: game.id,
                                userId: participantToRemove.participant.id,
                                // Display-only: the toast names the amount in
                                // the game's currency, not a bare number.
                                currency: game.paymentIntents?.[0]?.currency,
                            });
                        }
                        setParticipantToRemove(null);
                        onClose();
                    }
                }
            }
        );
    };

    const handleCancelRemove = () => {
        // Escape and the overlay both route here. Dismissing mid-flight would
        // recreate the original defect through a different door — the request
        // is already out and the operator would be back to a toast with no
        // surface. The ref rather than `isRemoving` for the same reason as
        // above, and so that this guard is reachable by a test at all: all
        // three real dismissal paths are ALSO closed by render state
        // (`isDisabled`, `closeOnEsc`, `closeOnOverlayClick`), which would
        // leave a render-state guard here permanently unfalsifiable.
        if (inFlightRef.current) {
            return;
        }
        setParticipantToRemove(null);
        onClose();
    };

    if (game.participants.length === 0) {
        return (
            <Box textAlign="center" py={8}>
                <Text fontSize="lg" color="gray.500">
                    {translate('game.no_participants')}
                </Text>
            </Box>
        );
    }

    return (
        <VStack spacing={4} align="stretch">
            <CoveredByChips participants={game.participants} />

            {/* Participants Grid */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {game.participants.map((participant) => (
                    <Box
                        key={participant.participant.id}
                        p={4}
                        borderWidth="1px"
                        borderRadius="lg"
                        bg={cardBg}
                        _hover={{
                            bg: cardHoverBg,
                            transform: 'translateY(-2px)',
                            boxShadow: 'md'
                        }}
                        transition="all 0.2s"
                        position="relative"
                    >
                        {/* Remove button — admin-only (API rejects coach kicks) */}
                        {isOrgManager && (
                            <Tooltip label={translate('game.remove_participant')}>
                                <IconButton
                                    aria-label={translate('game.remove_participant')}
                                    icon={<FaTrash />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="outline"
                                    position="absolute"
                                    top={2}
                                    right={2}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveClick(participant);
                                    }}
                                    // The mutation flag is shared by the whole
                                    // grid, so a bare `isLoading` here put a
                                    // spinner on EVERY participant's remove
                                    // button while one of them was being
                                    // removed — eleven rows claiming a removal
                                    // that is happening to one. Spin the row
                                    // the request is actually about; keep the
                                    // others disabled, because the reason they
                                    // were disabled (no concurrent kicks) is
                                    // still true.
                                    isLoading={isRemoving && participantToRemove?.participant.id === participant.participant.id}
                                    isDisabled={isRemoving}
                                />
                            </Tooltip>
                        )}

                        <VStack
                            spacing={3}
                            cursor={navigateToUserDetails ? "pointer" : undefined}
                            onClick={navigateToUserDetails ? () => navigateToUserDetails(participant.participant.id) : undefined}
                        >
                            <Avatar
                                size="md"
                                name={`${participant.participant.firstName} ${participant.participant.lastName}`}
                                src={participant.participant.avatar}
                            />

                            <VStack spacing={1} textAlign="center">
                                <Text fontWeight="semibold" fontSize="md">
                                    {participant.participant.firstName} {participant.participant.lastName}
                                </Text>
                                <Text fontSize="sm" color="gray.500">
                                    {participant.participant.email}
                                </Text>
                                <HStack spacing={2}>
                                    <Badge colorScheme="brand" size="sm">
                                        {translate('game.level')}: {participant.participant.level}
                                    </Badge>
                                    <Badge colorScheme="purple" size="sm">
                                        #{participant.position + 1}
                                    </Badge>
                                    {participant.participant.isGuest && (
                                        <Badge colorScheme="orange" size="sm">
                                            {translate('game.guest')}
                                        </Badge>
                                    )}
                                    {participant.pendingAcceptance && (
                                        <Badge colorScheme="yellow" size="sm">
                                            {translate('game.awaiting_acceptance')}
                                        </Badge>
                                    )}
                                </HStack>
                            </VStack>

                            <Text fontSize="xs" color="gray.400">
                                {translate('game.joined_at')}: {formatDate(new Date(participant.createdAt))}
                            </Text>
                        </VStack>
                    </Box>
                ))}
            </SimpleGrid>

            {/* Confirmation Dialog */}
            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={handleCancelRemove}
                closeOnOverlayClick={!isRemoving}
                closeOnEsc={!isRemoving}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {translate('game.remove_participant_title')}
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            {participantToRemove && (
                                <>
                                    {translate('game.remove_participant_message')} <strong>
                                        {participantToRemove.participant.firstName} {participantToRemove.participant.lastName}
                                    </strong>?
                                    <Text fontSize="sm" color="gray.600" mt={2}>
                                        {translate('game.remove_participant_refund_info')}
                                    </Text>
                                    <Checkbox
                                        mt={4}
                                        isChecked={refundSeatAfterRemove}
                                        isDisabled={isRemoving}
                                        onChange={(e) => setRefundSeatAfterRemove(e.target.checked)}
                                        colorScheme="brand"
                                    >
                                        {translate('game.remove_and_refund_seat')}
                                    </Checkbox>
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                        {translate('game.remove_and_refund_seat_hint')}
                                    </Text>
                                </>
                            )}
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button
                                ref={cancelRef}
                                onClick={handleCancelRemove}
                                isDisabled={isRemoving}
                            >
                                {translate('common.cancel')}
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleConfirmRemove}
                                ml={3}
                                isLoading={isRemoving}
                            >
                                {translate('game.remove_participant')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </VStack>
    );
};

export default ParticipantsCard;
